import { createColumnHelper } from "@tanstack/react-table";
import { formatIncompletePhoneNumber } from "libphonenumber-js";
import { Option } from "rc-select";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "reactstrap";
import Button from "../../components/Button";
import { USER_MANAGE_ABILITY_OPTIONS } from "../../components/Inputs/ManageAbilitySelect";
import ResourceSelect from "../../components/Inputs/ResourceSelect";
import Select from "../../components/Inputs/Select";
import {
    getSettingsOrgResourceSearchFilters,
    getSettingsResourceSearchFilters,
    renderCompanyResourceLabel,
    renderOrgLevelResourceLabel,
    renderPeopleCustomFieldLabel,
    renderSettingsResourceLabel,
} from "../../utils/helpers/settings";
import { firstToUpper } from "../../utils/helpers/string";
import {
    getPrimaryActiveResource,
    renderUserName,
} from "../../utils/helpers/user";
import { useAccess } from "../../utils/hooks/access";
import { useVisible } from "../../utils/hooks/useVisible";
import { getCompaniesSearchFilters } from "../Profile/Personal/Actions/ChangeCompanyInformation";
import { useCustomFields } from "../Settings/CustomFields/context";
import { OrgLevelGroupsContext } from "../Settings/General/OrganisationLevelGroups/context";
import { useFetchRoles, useImpersonate } from "./api";

export const getUserSearchFilters = (search) => ({
    search,
    fields: ["id", "firstName", "lastName"],
    withoutLookups: "true",
});

const columnHelper = createColumnHelper();

function UserGroupColumn({ userGroups, id }) {
    const { t } = useTranslation();
    const { visible, toggle } = useVisible();

    return (
        <>
            <span id={`user-groups-tooltip-${id}`}>
                {t("user-groups-count", {
                    count: userGroups?.length,
                })}
            </span>

            {userGroups?.length > 0 && (
                <Tooltip
                    isOpen={visible}
                    toggle={toggle}
                    trigger="hover"
                    target={`user-groups-tooltip-${id}`}
                    className="user-groups-tooltip"
                >
                    <div className="d-flex flex-column">
                        {userGroups?.map((group) => (
                            <span className="text-sm font-weight-bolder px-2 m-2">
                                {renderSettingsResourceLabel(group)}
                            </span>
                        ))}
                    </div>
                </Tooltip>
            )}
        </>
    );
}

export const useColumns = ({ verify, loadingVerify }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { groups } = useContext(OrgLevelGroupsContext);
    const { login, userBeingImpersonated } = useImpersonate();
    const { peopleCustomFields } = useCustomFields();

    const { roles } = useFetchRoles();

    const { hasAccess: canManageUsers } = useAccess("canManageUsers");
    const { hasAccess: canImpersonate } = useAccess("people.impersonate");

    const url = useMemo(() => {
        const query = new URLSearchParams();

        if (roles) {
            roles.forEach(
                (role) =>
                    role.permissions.canManageUsers &&
                    query.append("roleId", role.id)
            );
        }

        query.set("perPage", "20");
        query.set("includeSelf", "true");

        return `/users?${query.toString()}`;
    }, [roles]);

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("name", {
                enableColumnFilter: true,
                header: t("name"),
                cell: (info) => {
                    const { firstName, lastName } = info.row.original;
                    return (
                        firstName &&
                        lastName && (
                            <span
                                className="cursor-pointer text-primary"
                                onClick={() =>
                                    navigate(`/people/${info.row.original.id}`)
                                }
                            >
                                {renderUserName(info.row.original, false)}
                            </span>
                        )
                    );
                },
            }),
            columnHelper.accessor("employeeNumber", {
                enableColumnFilter: true,
                header: t("employee-number"),
            }),
            columnHelper.accessor("status", {
                enableColumnFilter: true,
                header: t("status"),
                cell: (info) => firstToUpper(info.getValue()),
                filterType: "select",
                Filter: (
                    <Select showSearch>
                        <Option value="active" key="active">
                            {t("active")}
                        </Option>

                        <Option value="inactive" key="inactive">
                            {t("inactive")}
                        </Option>

                        <Option value="suspended" key="suspended">
                            {t("suspended")}
                        </Option>

                        <Option value="leaveOfAbsence" key="leaveOfAbsence">
                            {t("leave-of-absence")}
                        </Option>

                        <Option value="retired" key="retired">
                            {t("retired")}
                        </Option>

                        <Option value="terminated" key="terminated">
                            {t("terminated")}
                        </Option>

                        <Option value="ready" key="ready">
                            {t("ready")}
                        </Option>

                        <Option value="unverified" key="unverified">
                            {t("unverified")}
                        </Option>
                    </Select>
                ),
            }),
            columnHelper.accessor("locations.locationId", {
                enableColumnFilter: true,
                header: t("location"),
                cell: (info) => {
                    const { locations } = info.row.original;
                    const location = getPrimaryActiveResource(locations);
                    return location && renderSettingsResourceLabel(location);
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/locations"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("jobs.jobId", {
                enableColumnFilter: true,
                header: t("job-title"),
                cell: (info) => {
                    const { jobs } = info.row.original;
                    const job = getPrimaryActiveResource(jobs);
                    return job && renderSettingsResourceLabel(job);
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/jobs"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("userGroups", {
                enableColumnFilter: true,
                header: t("groups"),
                cell: (info) => {
                    const { userGroups, id } = info.row.original;
                    return <UserGroupColumn userGroups={userGroups} id={id} />;
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/user-group"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("email", {
                enableColumnFilter: true,
                header: t("email"),
            }),
            columnHelper.accessor("phone", {
                enableColumnFilter: true,
                header: t("phone-number"),
                cell: (info) => {
                    const value = info.getValue();
                    if (value == null || value === "") {
                        return null;
                    }
                    const raw = String(value).trim();
                    if (!raw) {
                        return null;
                    }
                    const e164 = raw.startsWith("+") ? raw : `+${raw}`;
                    return formatIncompletePhoneNumber(e164);
                },
            }),
            columnHelper.accessor("supervisors.userId", {
                enableColumnFilter: true,
                header: t("supervisor"),
                cell: (info) => {
                    const { supervisors } = info.row.original;
                    const primarySupervisor = supervisors?.find(
                        (supervisor) => supervisor.primary
                    );
                    if (!primarySupervisor) {
                        return null;
                    }
                    return (
                        <div className="text-left">
                            {renderUserName(primarySupervisor)}
                        </div>
                    );
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="firstName"
                        renderLabel={renderUserName}
                        resourcePath={url}
                        mode="multiple"
                        hasSearch
                        getSearchFilters={getUserSearchFilters}
                    />
                ),
            }),
        ];

        if (groups) {
            groups.forEach((group) => {
                const level = `${group.level}`.replace("_", "");
                const column = columnHelper.accessor(level, {
                    enableColumnFilter: true,
                    header: group.description,
                    cell: (info) => {
                        const value = info.getValue();
                        return value && renderOrgLevelResourceLabel(value);
                    },
                    filterType: "resource-select",
                    Filter: (
                        <ResourceSelect
                            labelPropName="description"
                            resourcePath={`${group.id}/org-levels`}
                            mode="multiple"
                            renderLabel={renderOrgLevelResourceLabel}
                            getSearchFilters={
                                getSettingsOrgResourceSearchFilters
                            }
                            valuePropName="id"
                            hasSearch
                        />
                    ),
                });
                columns.push(column);
            });
        }

        const resourceListCell =
            (labelFn = renderSettingsResourceLabel) =>
                (info) => {
                    const value = info.getValue();
                    if (!value) {
                        return null;
                    }
                    const list = Array.isArray(value) ? value : [value];
                    return list
                        .map((item) => labelFn(item))
                        .filter(Boolean)
                        .join(", ");
                };

        columns.push(
            columnHelper.accessor("payGroup", {
                enableColumnFilter: true,
                header: t("pay-group"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/pay-group"
                        renderLabel={renderSettingsResourceLabel}
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("employeeType", {
                enableColumnFilter: true,
                header: t("employee-type"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/employee-type"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("project", {
                enableColumnFilter: true,
                header: t("project"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/project"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("union", {
                enableColumnFilter: true,
                header: t("union"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/union"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("companies", {
                enableColumnFilter: true,
                header: t("companies"),
                cell: resourceListCell(renderCompanyResourceLabel),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/companies"
                        hasSearch
                        getSearchFilters={getCompaniesSearchFilters}
                        mode="multiple"
                        renderLabel={renderCompanyResourceLabel}
                    />
                ),
            }),
            columnHelper.accessor("calculationGroup", {
                enableColumnFilter: true,
                header: t("calculation-group"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/calculation-group"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("roles", {
                enableColumnFilter: true,
                header: t("roles"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/user-roles"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("holiday", {
                enableColumnFilter: true,
                header: t("holiday"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/holiday"
                        hasSearch
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("groupings", {
                enableColumnFilter: true,
                header: t("groupings"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/grouping?excludedResources=pay-code"
                        mode="multiple"
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
            columnHelper.accessor("employmentType", {
                enableColumnFilter: true,
                header: t("employment-type"),
                cell: resourceListCell(),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        resourcePath="/user-demographic/employment-type"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("salaryOrHourly", {
                enableColumnFilter: true,
                header: t("salary-or-hourly"),
                cell: (info) => {
                    const v = info.getValue();
                    if (v === "S") {
                        return t("salaried");
                    }
                    if (v === "H") {
                        return t("hourly");
                    }
                    return null;
                },
                filterType: "select",
                Filter: (
                    <Select>
                        <Select.Option value="S">{t("salaried")}</Select.Option>
                        <Select.Option value="H">{t("hourly")}</Select.Option>
                    </Select>
                ),
            }),
            columnHelper.accessor("manageAbility", {
                enableColumnFilter: true,
                header: t("manage-ability"),
                cell: (info) => {
                    const v = info.getValue();
                    if (!v) {
                        return null;
                    }
                    const list = Array.isArray(v) ? v : [v];
                    return list.map((item) => t(item)).join(", ");
                },
                filterType: "select",
                Filter: (
                    <Select mode="multiple">
                        {USER_MANAGE_ABILITY_OPTIONS.map(({ value, label }) => (
                            <Select.Option key={value} value={value}>
                                {t(label)}
                            </Select.Option>
                        ))}
                    </Select>
                ),
            }),
        );

        if (peopleCustomFields?.length > 0) {
            peopleCustomFields.forEach((customField) => {
                const column = columnHelper.accessor(`customFields.${customField.code}`, {
                    header: renderPeopleCustomFieldLabel(customField),
                    cell: ({ row: { original } }) => {
                        const value = original?.customFields?.[customField.code];
                        if (value === undefined || value === null || value === "") {
                            return "--";
                        }
                        return value;
                    }
                });
                columns.push(column);
            });
        }

        if (canManageUsers || canImpersonate) {
            const actions = columnHelper.display({
                id: "actions",
                enableHiding: false,
                enableColumnFilter: false,
                header: () => <div className="text-right">{t("actions")}</div>,
                cell: (info) => {
                    const { id, status } = info.row.original;

                    return (
                        <div className="d-flex justify-content-between align-items-center">
                            {canImpersonate && (
                                <Button
                                    className="bg-transparent shadow-none border-0 text-primary p-0"
                                    onClick={() =>
                                        login(id, () => navigate("/redirect"))
                                    }
                                    loading={userBeingImpersonated === id}
                                    disabled={
                                        loadingVerify ||
                                        (userBeingImpersonated &&
                                            id !== userBeingImpersonated)
                                    }
                                >
                                    <i className="fa-solid fa-right-to-bracket mr-1" />
                                    {t("log-in")}
                                </Button>
                            )}

                            {status === "unverified" && verify && canManageUsers && (
                                <Button
                                    className="bg-transparent shadow-none border-0 text-info p-0"
                                    onClick={() => verify(info.row.original)}
                                    loading={loadingVerify}
                                    disabled={userBeingImpersonated === id}
                                >
                                    <i className="fa-solid fa-circle-check mr-1" />
                                    {t("verify")}
                                </Button>
                            )}
                        </div>
                    );
                },
            });
            columns.push(actions);
        }

        return columns;
    }, [
        peopleCustomFields,
        canManageUsers,
        canImpersonate,
        groups,
        url,
        loadingVerify,
        login,
        verify,
        navigate,
        t,
        userBeingImpersonated,
    ]);
};
